import { useCallback, useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PhoneInput,
  defaultCountries,
  parseCountry,
  type CountryIso2,
} from 'react-international-phone';
import 'react-international-phone/style.css';
import { DEFAULT_COUNTRY, EMAIL_OTP_ENABLED, SMS_COUNTRIES } from '@/lib/supported-countries';
import { cn } from '@/lib/cn';

export type IdentityKind = 'phone' | 'email';
export interface IdentityValue {
  kind: IdentityKind;
  /** Phone: E.164 (e.g. +14165551234). Email: trimmed, lowercased. */
  value: string;
}

interface Props {
  value: IdentityValue;
  onChange: (next: IdentityValue) => void;
  error?: string;
  disabled?: boolean;
}

/**
 * Tabbed phone-or-email input.
 *
 * - "Phone" tab: country picker (Twilio-supported countries only) +
 *   national-format input. The wrapped `<PhoneInput>` does E.164
 *   normalisation as the user types; we never see the raw national form.
 * - "Email" tab: standard email input.
 *
 * Replaces the old free-text "phone or email" field — that one made
 * users guess the country code and couldn't reliably classify pasted
 * values. The kind is now explicit so the parent doesn't have to.
 */
export function PhoneEmailInput({ value, onChange, error, disabled }: Props): React.JSX.Element {
  const { t } = useTranslation();
  const id = useId();

  // Country list trimmed to whatever Twilio is enabled for. Default first.
  const allowedCountries = useMemo(
    () =>
      defaultCountries.filter((c) => SMS_COUNTRIES.includes(parseCountry(c).iso2 as CountryIso2)),
    [],
  );

  const setKind = useCallback(
    (kind: IdentityKind) => {
      if (kind === value.kind) return;
      // Reset value when toggling tabs — partial entries don't carry over.
      onChange({ kind, value: '' });
    },
    [value.kind, onChange],
  );

  return (
    <div className="w-full">
      {EMAIL_OTP_ENABLED && <Segmented kind={value.kind} onChange={setKind} disabled={disabled} />}

      {value.kind === 'phone' || !EMAIL_OTP_ENABLED ? (
        <div
          className={cn(
            'rounded-xl border bg-card transition',
            EMAIL_OTP_ENABLED && 'mt-3',
            error ? 'border-error' : 'border-border',
            disabled && 'cursor-not-allowed opacity-60',
          )}
          // CSS variables theme the lib to our brand tokens — both the
          // resting input + the dropdown menu (open state). The lib's
          // default palette is iOS-blue; without the dropdown vars the
          // open menu pops white-on-white on the dark theme and shows
          // a bright blue selected row that fights the brand teal.
          style={
            {
              // Resting input
              '--react-international-phone-height': '3.125rem', // matches h-12.5
              '--react-international-phone-font-size': '1rem',
              '--react-international-phone-border-radius': '0.75rem',
              '--react-international-phone-border-color': 'transparent',
              '--react-international-phone-background-color': 'transparent',
              '--react-international-phone-text-color': 'var(--color-foreground)',
              '--react-international-phone-flag-width': '1.25rem',
              '--react-international-phone-flag-height': '1.25rem',
              // Country selector (closed)
              '--react-international-phone-country-selector-arrow-color': 'var(--color-muted)',
              '--react-international-phone-country-selector-background-color': 'transparent',
              '--react-international-phone-country-selector-background-color-hover':
                'color-mix(in oklab, var(--color-primary) 10%, transparent)',
              // Dropdown menu (open)
              '--react-international-phone-dropdown-shadow': '0 12px 32px -8px rgb(0 0 0 / 0.18)',
              '--react-international-phone-dropdown-item-height': '2.5rem',
              '--react-international-phone-dropdown-item-background-color': 'var(--color-card)',
              '--react-international-phone-dropdown-item-text-color': 'var(--color-foreground)',
              '--react-international-phone-dropdown-item-dial-code-color': 'var(--color-muted)',
              '--react-international-phone-dropdown-item-background-color-focus':
                'color-mix(in oklab, var(--color-primary) 12%, transparent)',
              '--react-international-phone-selected-dropdown-item-background-color':
                'color-mix(in oklab, var(--color-primary) 16%, transparent)',
              '--react-international-phone-selected-dropdown-item-text-color':
                'var(--color-foreground)',
              '--react-international-phone-selected-dropdown-item-dial-code-color':
                'var(--color-muted)',
            } as React.CSSProperties
          }
        >
          <PhoneInput
            inputProps={{
              id: `${id}-phone`,
              'aria-invalid': Boolean(error),
              'aria-describedby': error ? `${id}-error` : undefined,
              'aria-label': t('welcome.cta.phone'),
            }}
            defaultCountry={DEFAULT_COUNTRY}
            countries={allowedCountries}
            disabled={disabled}
            value={value.value}
            onChange={(phone) => onChange({ kind: 'phone', value: phone })}
            forceDialCode
          />
        </div>
      ) : (
        <input
          id={`${id}-email`}
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="off"
          spellCheck={false}
          placeholder={t('welcome.cta.email')}
          value={value.value}
          disabled={disabled}
          onChange={(e) => onChange({ kind: 'email', value: e.target.value })}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${id}-error` : undefined}
          className={cn(
            'mt-3 h-12.5 w-full rounded-xl border bg-card px-4 text-base text-foreground placeholder:text-muted',
            'focus:outline-none focus:ring-2 focus:ring-primary/40',
            error ? 'border-error' : 'border-border',
            disabled && 'cursor-not-allowed opacity-60',
          )}
        />
      )}

      {error && (
        <p id={`${id}-error`} className="mt-2 text-sm text-error">
          {error}
        </p>
      )}
    </div>
  );
}

function Segmented({
  kind,
  onChange,
  disabled,
}: {
  kind: IdentityKind;
  onChange: (k: IdentityKind) => void;
  disabled?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      role="tablist"
      aria-label={t('welcome.cta.identityKind')}
      className="grid grid-cols-2 gap-1 rounded-xl bg-background p-1"
    >
      {(['phone', 'email'] as IdentityKind[]).map((k) => (
        <button
          key={k}
          type="button"
          role="tab"
          aria-selected={kind === k}
          disabled={disabled}
          onClick={() => onChange(k)}
          className={cn(
            'h-10 rounded-lg text-sm font-medium transition',
            kind === k ? 'bg-card text-foreground shadow-sm' : 'text-muted hover:text-foreground',
            disabled && 'cursor-not-allowed opacity-60',
          )}
        >
          {t(k === 'phone' ? 'welcome.cta.phone' : 'welcome.cta.email')}
        </button>
      ))}
    </div>
  );
}
